import React, { useState } from "react";
// images
import CalendarIcon from "../../Assets/images/calendar.svg";
import CloseIcon from "../../Assets/images/close.svg";
import SearchIcon from "../../Assets/images/search.svg";
import Date from "../../Components/FormControl/Date/Date";
import RadioBox from "Components/FormControl/RadioBox/RadioBox";
import styles from "./index.module.scss";
import CheckBox from "Components/FormControl/CheckBox/CheckBox";
import Select from "Components/FormControl/Select/Select";
import { DATE_FILTER_TYPE, DATE_TYPE, DEFAULT_TIMEZONE } from "Helpers/Constants";
import moment from "moment";
import { convertDateTime } from "Helpers/date";
import momentTimezone from "moment-timezone";
import ExportIcon from "./../../Assets/images/Export.svg";
import { CSVLink } from "react-csv";

const ROLLING_UNIT = { weekly: "week", monthly: "month", yearly: "year" };
// [units back from now, unit]; today and yesterday use the store timezone.
const PRESET_RANGE = {
  today: [0, "day"],
  yesterday: [1, "day"],
  weekly: [0, "isoWeek"],
  monthly: [0, "month"],
  thisMonth: [0, "month"],
  lastMonth: [1, "month"],
  yearly: [0, "year"],
  thisYear: [0, "year"],
  lastYear: [1, "year"],
};

const GlobalDateFilters = ({
  isAllCheckStoreBnt,
  isFilterByOption,
  isDateFilter,
  isClearSearchBtn,
  isSearchBtn,
  isExportBtn,
  isFilterByPeriod,
  isSearchIcon,
  storeTimezone,
  isSelectedPeriodLabel,
  ...props
}) => {
  const defaultFilterByOption = [
    {
      label: "Order date",
      value: "order_date",
    },
    {
      label: "Delivery date",
      value: "delivery_date",
    },
  ];
  const filterByOptions = props?.filterByOptions
    ? [...props?.filterByOptions]
    : defaultFilterByOption;

  const defaultPeriodOptions = [
    {
      title: "Weekly",
      id: "weekly",
    },
    {
      title: "Monthly",
      id: "monthly",
    },
    {
      title: "Yearly",
      id: "yearly",
    },
  ];

  const periodOptions = props?.periodOptions
    ? [...props?.periodOptions]
    : defaultPeriodOptions;

  const setRange = (from, to, format) => {
    props.setFromDate(moment(from).format(format));
    props.setToDate(moment(to).format(format));
  };

  // A rolling period runs from the picked date, or back to it when the To date was picked.
  const handleDateSelectionFilter = (date = "", type = "", format = "YYYY-MM-DD", datePeriod = null) => {
    const isTo = type === DATE_TYPE.TO_DATE;
    if (!datePeriod) return isTo ? props.setToDate(date) : props.setFromDate(date);
    const unit = ROLLING_UNIT[datePeriod];
    if (unit && isTo) return setRange(moment(date).subtract(1, unit).add(1, "day"), date, format);
    if (unit) return setRange(date, moment(date).add(1, unit).subtract(1, "day"), format);
    if (datePeriod !== "custom") return setRange(props.fromDate, props.toDate, format);
    setRange(isTo ? props.fromDate : date, isTo ? date : props.toDate, format);
  };

  const handleSelectedPeriod = (datePeriod, format = "YYYY-MM-DD") => {
    const [back, unit] = PRESET_RANGE[datePeriod] || [];
    if (!unit) return setRange(props.fromDate, props.toDate, format);
    const now = unit === "day" ? momentTimezone.tz(storeTimezone || DEFAULT_TIMEZONE) : moment();
    now.subtract(back, unit);
    setRange(now.clone().startOf(unit), now.endOf(unit), format);
  };

  const handleSelectedPeriodLabel = (data) => {
    if (isSelectedPeriodLabel) {
      const label = periodOptions.find((option) => option.id === data)?.label;
      if(label !== DATE_FILTER_TYPE.custom){
        props.setSelectedPeriodLabel(label);
      }else{
        props.setSelectedPeriodLabel(props?.selectedPeriodLabel);
      }
    }
  };

  const reference = React.useRef(null);
  const actionsLayout = isSearchBtn ? "col-sm-4 col-md-12 col-lg-4 flex-wrap" : "col-sm-3 flex-wrap flex-xl-nowrap";

  const [filtersOpen, setFiltersOpen] = useState(false);
  const typeLabel = filterByOptions.find((o) => o.value === props.type)?.label;
  const dates = [props.fromDate, props.toDate].filter(Boolean).map((d) => moment(d).format("MMM D"));
  const range = [...new Set(dates)].join(" to ");
  const filterSummary = [[typeLabel, range].filter(Boolean).join(": "), props.defaultSetCheckAllStore && "All Stores"]
    .filter(Boolean)
    .join(", ");

  return (
    <div className={`row d-flex align-items-end ${isSearchBtn ? `mr-0 ${styles.searchRow}` : ""} ${styles.filterRow}`}>
      <button
        type="button"
        className={styles.filtersToggle}
        aria-expanded={filtersOpen}
        onClick={() => setFiltersOpen(!filtersOpen)}
      >
        <span className={styles.filtersToggleTitle}>Filters</span>
        <span className={styles.filtersSummary}>{filterSummary}</span>
      </button>
      <div className={`col-sm-12 col-xs-12 ${filtersOpen ? "" : styles.filtersCollapsed}`}>
        <div className="col-12 d-flex align-items-center p-0">
          {isFilterByOption && (
            <div className="col-12 col-md-6 d-flex align-items-center flex-wrap flex-md-nowrap">
              <span
                className="mb-0 mt-2 pb-0 d-inline-bock mr-3"
                style={{ lineHeight: 1, whiteSpace: "nowrap" }}
              >
                Filter By
              </span>
              {filterByOptions.map((option) => (
                <span style={{ whiteSpace: "nowrap" }}>
                  <RadioBox
                    key={option.value}
                    selectedValue={props.type}
                    onChange={() => {
                      if (props.setType) {
                        props.setType(option.value);
                      }
                    }}
                    label={option.label}
                    value={option.value}
                  />
                </span>
              ))}
            </div>
          )}
        </div>
        <div className={`col-12 d-flex align-items-center p-0 ${isSearchBtn ? "flex-wrap" : ""} ${styles.filterControls}`}>
          {isFilterByPeriod && (
            <div className="col-sm-2 col-xs-12">
              <Select
                label={isSelectedPeriodLabel ? props?.selectedPeriodLabel : ""}
                value={props?.selectedPeriod}
                onChange={(data) => {
                  props.setSelectedPeriod(data);
                  handleSelectedPeriod(data, props.dateFormate);
                  isSelectedPeriodLabel && handleSelectedPeriodLabel(data);
                }}
                options={periodOptions}
                disabled={false}
              />
            </div>
          )}

          {isDateFilter && (
            <>
              <div className={`${isSearchBtn ? "col-sm-5 col-md-4 col-lg-3" : "col-sm-4"} col-xs-12 ${styles.dateField}`}>
                <Date
                  label={props?.fromDateLabel || "Filter From"}
                  value={convertDateTime(
                    moment(props.fromDate).format("YYYY-MM-DD"),
                    "iso4",
                    "ccc LLL dd, yyyy"
                  )}
                  dateFormat="ddd MMM DD, YYYY"
                  type="date"
                  fieldIcon={CalendarIcon}
                  onChange={(fromDate) => {
                    handleDateSelectionFilter(
                      fromDate,
                      DATE_TYPE.FROM_DATE,
                      props.dateFormate,
                      isFilterByPeriod ? props?.selectedPeriod : null
                    );
                  }}
                  disabled={props?.isDisabled || false}
                  isTimeStamp={false}
                />
              </div>
              <div className={`${isSearchBtn ? "col-sm-5 col-md-4 col-lg-3" : "col-sm-3"} col-xs-12 ${styles.dateField}`}>
                <Date
                  label={props?.toDateLabel || "Filter To"}
                  value={convertDateTime(
                    moment(props.toDate).format("YYYY-MM-DD"),
                    "iso4",
                    "ccc LLL dd, yyyy"
                  )}
                  dateFormat="ddd MMM DD, YYYY"
                  type="date"
                  fieldIcon={CalendarIcon}
                  onChange={(toDate) => {
                    handleDateSelectionFilter(
                      toDate,
                      DATE_TYPE.TO_DATE,
                      props.dateFormate,
                      isFilterByPeriod ? props?.selectedPeriod : null
                    );
                  }}
                  disabled={props?.isDisabled || false}
                  isTimeStamp={false}
                />
              </div>
            </>
          )}
          <div className={`${actionsLayout} col-xs-12 d-flex align-items-center ${styles.filterActions}`}>
            <div className="td-actions d-flex align-items-center flex-wrap flex-xl-nowrap">
              {isDateFilter && (
                <button
                  className="btn gray-btn d-inline-block mb-1 mr-2 mt-0"
                  style={{ whiteSpace: "nowrap", height: "34px", padding: "0 12px"}}
                  onClick={() => {
                    handleSelectedPeriod("today");
                    const today = momentTimezone.tz(storeTimezone || DEFAULT_TIMEZONE).format("YYYY-MM-DD");
                    props?.fetchData({ fromDateDefault: today, toDateDefault: today });
                  }}
                >
                  Today
                </button>
              )}
              {isAllCheckStoreBnt && (
                <span className="ml-3" style={{ whiteSpace: "nowrap" }}>
                  <CheckBox
                    value={props.defaultSetCheckAllStore}
                    label="All Stores"
                    onChange={(data) => {
                      props?.checkAllStore(data);
                    }}
                  />
                </span>
              )}
            </div>
            <div className="td-actions pt-2 d-flex align-items-center">
              {isSearchIcon && (
                <span
                  onClick={(e) => props?.fetchData()}
                  className={`link cursor-pointer ${styles.searchIconWrapper}`}
                >
                  <img className={styles.searchIcon} src={SearchIcon} alt="" />
                </span>
              )}
              {isSearchBtn && (
                <button
                  className="btn gray-btn d-inline-block mb-1 mr-2 mt-0"
                  onClick={() => props?.fetchData()}
                >
                  Search
                </button>
              )}

              {isClearSearchBtn && (
                <span
                  onClick={(e) => props?.handleClearSearch(e)}
                  className={`link cursor-pointer ml-3 ${styles.clearIconWrapper}`}
                >
                  <img className={styles.clearIcon} src={CloseIcon} alt="" />
                </span>
              )}

              {isExportBtn && (
                <>
                  {props?.exportData && props?.exportData.length > 0 && (
                    <CSVLink
                      ref={reference}
                      style={{ display: "none" }}
                      data={props?.exportData}
                      headers={props?.exportHeader}
                      filename={"orderLookup.csv"}
                      className={`link csvLink cursor-pointer ml-3 ${styles.searchIconWrapper}`}
                    >
                      DownLoad
                    </CSVLink>
                  )}
                  <span
                    onClick={props?.handleExport}
                    className={`link cursor-pointer ml-3 ${styles.searchIconWrapper}`}
                  >
                    <img className={styles.searchIcon} src={ExportIcon} alt="" />
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalDateFilters;
